import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";

import { queryClient } from "./lib/queryClient";
import { startOutboxSync } from "./lib/outbox";
import { registerServiceWorker } from "./lib/pwa";

import { AuthProvider, useAuth } from "./features/auth/AuthProvider";
import { LoginPage } from "./features/auth/LoginPage";
import { AppShell } from "./components/AppShell";
import { ToastProvider, useToast } from "./components/Toast";
import { Spinner } from "./components/ui";

import { FeedPage } from "./features/feed/FeedPage";
import { GoalsPage } from "./features/goals/GoalsPage";
import { GoalFormPage } from "./features/goals/GoalFormPage";
import { GoalDetailPage } from "./features/goals/GoalDetailPage";
import { ProfilePage } from "./features/profile/ProfilePage";
import { SettingsPage } from "./features/profile/SettingsPage";
import { useRealtime } from "./features/feed/useRealtime";

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <Root />
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

function Root() {
  const { session, loading } = useAuth();
  const toast = useToast();
  const [updateReady, setUpdateReady] = useState(false);
  const applyUpdate = useRef<(() => void) | null>(null);

  // Keep the group's data live.
  useRealtime(Boolean(session));

  // Drain the offline queue whenever there is any reason to think it might work.
  useEffect(() => {
    if (!session) return;
    return startOutboxSync((count) => {
      toast(`Synced ${count} check-in${count === 1 ? "" : "s"}`, "success");
    });
  }, [session, toast]);

  useEffect(() => {
    applyUpdate.current = registerServiceWorker(() => setUpdateReady(true));
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner className="size-6" />
      </div>
    );
  }

  if (!session) return <LoginPage />;

  return (
    <Routes>
      <Route
        element={
          <AppShell
            banner={
              updateReady ? (
                <button
                  type="button"
                  onClick={() => applyUpdate.current?.()}
                  className="w-full bg-accent px-4 py-2 text-sm font-medium text-white"
                >
                  A new version is ready — tap to reload
                </button>
              ) : null
            }
          />
        }
      >
        <Route index element={<FeedPage />} />
        <Route path="goals" element={<GoalsPage />} />
        <Route path="goals/new" element={<GoalFormPage />} />
        <Route path="goals/:goalId" element={<GoalDetailPage />} />
        <Route path="goals/:goalId/edit" element={<GoalFormPage />} />
        <Route path="people/:personId" element={<ProfilePage />} />
        <Route path="me" element={<MyProfileRedirect />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

/** The "Me" tab is just this user's profile page. */
function MyProfileRedirect() {
  const { userId } = useAuth();
  useParams(); // keeps the router happy about the nested route
  return userId ? <Navigate to={`/people/${userId}`} replace /> : <Navigate to="/" replace />;
}
